import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Validators, UntypedFormGroup, UntypedFormControl } from '@angular/forms';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Actions, ofType } from '@ngrx/effects';
import { iif, Observable, of } from 'rxjs';
import { filter, tap, takeUntil, debounceTime, switchMap, take, concatAll } from 'rxjs/operators';

import { IAuroraState } from '@apps/aurora.state';
import * as fromIts2Store from '@apps/its2/store';
import * as fromRootStore from '@apps/root/store';
import { SelectOption } from '@ui/components/form/common';
import { CreateService, Step1 } from '@apps/its2/models/create-service';
import { TreeModel } from '@ui/components/form/controls/treeview';
import { LanguageService } from '@common/services';
import { InputTreeview2Component } from '@ui/components/form/controls/input/input-treeview2/input-treeview2.component';
import { Its2Constants } from '@apps/its2/its2.constants';
import { groupRadioButtonValidator } from '@common/validators';
import { NextParams, StepState } from '@ui/misc/wizard/models';
import { CatalogEntry } from '@apps/its2/models';
import { CatalogEntryQuestion } from '@apps/its2/domain/models';
import { AppConstants } from '@apps/app.constants';

import { CreateServiceBase } from '../common/create-service-base';

@Component({
    selector: 'aa-create-service-step1',
    templateUrl: './create-service-step1.component.html',
    styleUrls: [
        './create-service-step1.component.scss',
        '../../request-create.scss'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreateServiceStep1Component extends CreateServiceBase implements OnInit
{
    private static stepIdx: number = 0;
    @ViewChild('inputTreeview', { static: true }) inputTreeview: InputTreeview2Component;
    public catalog: Observable<TreeModel>;
    public readonly titleField: string = 'title';
    public readonly urgencyIdField: string = 'urgencyId';
    public readonly catalogEntryField: string = 'catalogGuid';
    public urgencyOptions: SelectOption[];
    public step1Info: boolean = false;
    public isProcessing: boolean = false;
    public stepForm: UntypedFormGroup;
    public step1: Step1;
    private createService: CreateService;

    constructor (
        translateService: TranslateService,
        private store: Store<IAuroraState>,
        private actions: Actions,
        private languageService: LanguageService
    )
    {
        super(translateService);

        this.step1 = new Step1(translateService.currentLang);

        // this.store.dispatch(
        //     fromRootStore.RootMiscStoreActions.RootWizard2StepState({
        //         stepIdx: CreateServiceStep1Component.stepIdx,
        //         valid: false
        //     })
        // );
        this.catalog = this.store.select(fromIts2Store.getIts2CatalogsService, this.languageService.getShortItsmLanguage());
        this.urgencyOptions = Its2Constants.getUrgencyOptions(this.translateService);
        this.store.select(fromIts2Store.getCreateService)
            .pipe(
                filter((cs: CreateService): cs is CreateService => !!cs),
                tap((cs: CreateService) =>
                {
                    this.createService = cs;
                    this.step1 = new Step1(translateService.currentLang, cs.step1);

                    if (!this.stepForm)
                    {
                        this.createForm();
                    }
                    else if (cs.step1?.catalogEntry && !this.stepForm.get(this.catalogEntryField).value)
                    {
                        this.stepForm.patchValue({
                            [this.catalogEntryField]: cs.step1.catalogEntry
                        });
                    }

                    this.store.dispatch(
                        fromRootStore.RootMiscStoreActions.RootWizard2StepState(
                            new StepState(CreateServiceStep1Component.stepIdx, cs.step1.valid)
                        )
                    );
                })
            )
            .pipe(takeUntil(this.destroyator))
            .subscribe();
        /**
         * Pour tester directement l'accès au step2
         */
        const debugSteps: boolean = false;
        if (debugSteps && AppConstants.IsDebugStage)
        {
            // avec questions : c0fe3155-2fdf-4dcd-a91c-8a631a322691
            const catalogGuid = 'c0fe3155-2fdf-4dcd-a91c-8a631a322691';
            // sans question
            // const catalogGuid =  'c1430210-9176-48de-b463-ce8fb91ef1b3'

            this.store.dispatch(
                fromIts2Store.Its2CatalogsActions.Its2CatalogsServiceQuestionsRequested({
                    catalogGuid: catalogGuid
                })
            );

            this.store.select(fromIts2Store.getAllCatalogsService)
                .pipe(
                    filter((ces: CatalogEntry[]) =>
                        ces.length !== 0
                    ),
                    take(1),
                    concatAll(),
                    filter((ce: CatalogEntry) =>
                        ce.catalogGuid === catalogGuid
                    ),
                    tap((ce: CatalogEntry) =>
                    {
                        const step1 = new Step1(translateService.currentLang);
                        const nextParams = new NextParams(0, 1);

                        Object.assign(step1, {
                            title: 'Title test',
                            urgency: 1,
                            catalogEntry: ce
                        });
                        step1.valid = true;

                        setTimeout(() =>
                        {
                            this.store.dispatch(
                                fromIts2Store.Its2MiscActions.Its2RequestsServiceCreateStep1Exited({ step1 })
                            );
                            this.store.dispatch(
                                fromRootStore.RootMiscStoreActions.RootWizard2StepState(
                                    new StepState(CreateServiceStep1Component.stepIdx, true)
                                )
                            );
                            this.store.dispatch(
                                fromRootStore.RootMiscStoreActions.RootWizard2StepExitAccepted(nextParams)
                            );
                        }, 5000);
                    })
                )
                .subscribe();
        }
    }

    ngOnInit (): void
    {
        this.actions
            .pipe(
                ofType(fromRootStore.RootMiscStoreActions.RootWizard2StepExitRequested),
                filter((nextParams: NextParams) =>
                    nextParams.oldStepIdx === CreateServiceStep1Component.stepIdx
                ),
                switchMap((nextParams: NextParams) =>
                    this.onSubmit()
                        .pipe(
                            switchMap((isValid: boolean) =>
                                iif(
                                    () => isValid,
                                    of(true)
                                        .pipe(
                                            tap(() =>
                                            {
                                                this.store.dispatch(
                                                    fromIts2Store.Its2MiscActions.Its2RequestsServiceCreateStep1Exited({ step1: this.step1 })
                                                );
                                                this.store.dispatch(
                                                    fromRootStore.RootMiscStoreActions.RootWizard2StepState(
                                                        new StepState(CreateServiceStep1Component.stepIdx, true)
                                                    )
                                                );
                                                this.store.dispatch(
                                                    fromRootStore.RootMiscStoreActions.RootWizard2StepExitAccepted(nextParams)
                                                );
                                            })
                                        ),
                                    of(false)
                                        .pipe(
                                            tap(() =>
                                            {
                                                this.store.dispatch(
                                                    fromRootStore.RootMiscStoreActions.RootWizard2StepState(
                                                        new StepState(CreateServiceStep1Component.stepIdx, false)
                                                    )
                                                );
                                                this.store.dispatch(
                                                    fromRootStore.RootMiscStoreActions.RootWizard2StepExitRefused(nextParams)
                                                );
                                            })
                                        )
                                )
                            )
                        )
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    onSubmit = (): Observable<boolean> =>
    {
        this.markControlsDirty(this.stepForm);
        if (this.stepForm.invalid)
        {
            return of(false);
        }

        this.step1.valid = true;
        this.step1.title = this.stepForm.get(this.titleField).value;
        this.step1.urgency = this.stepForm.get(this.urgencyIdField).value;
        this.step1.catalogEntry = this.stepForm.get(this.catalogEntryField).value;

        return of(true);
    };

    private createForm ()
    {
        this.stepForm = new UntypedFormGroup({
            [this.titleField]: new UntypedFormControl(this.step1.title, Validators.required),
            [this.urgencyIdField]: new UntypedFormControl(this.step1.urgency, groupRadioButtonValidator),
            [this.catalogEntryField]: new UntypedFormControl(this.step1.catalogEntry, Validators.required)
        });
        this.formListener();
    }

    private formListener ()
    {
        this.stepForm.valueChanges
            .pipe(
                debounceTime(250),
                tap(() =>
                    this.store.dispatch(
                        fromRootStore.RootMiscStoreActions.RootDirtyPopinFormSet({ dirty: true })
                    )
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();

        this.stepForm.get(this.catalogEntryField).valueChanges
            .pipe(
                tap((catalog: CatalogEntry) => this.loadCatalogQuestions(catalog)),
                takeUntil(this.destroyator)
            )
            .subscribe();

        const initialCatalog = this.stepForm.get(this.catalogEntryField).value as CatalogEntry;
        if (initialCatalog)
        {
            this.loadCatalogQuestions(initialCatalog);
        }

        this.stepForm.statusChanges
            .pipe(
                debounceTime(250),
                tap((result: any) =>
                {
                    if (result === 'VALID')
                    {
                        this.store.dispatch(
                            fromRootStore.RootMiscStoreActions.RootWizard2StepState({
                                stepIdx: CreateServiceStep1Component.stepIdx,
                                valid: true
                            })
                        );
                    }
                    else
                    {
                        // INVALID, PENDING, or DISABLED
                        this.store.dispatch(
                            fromRootStore.RootMiscStoreActions.RootWizard2StepState({
                                stepIdx: CreateServiceStep1Component.stepIdx,
                                valid: false
                            })
                        );
                    }
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    private loadCatalogQuestions (catalog: CatalogEntry): void
    {
        if (!catalog?.catalogGuid)
        {
            return;
        }

        this.store.dispatch(
            fromIts2Store.Its2CatalogsActions.Its2CatalogsServiceQuestionsRequested({
                catalogGuid: catalog.catalogGuid
            })
        );
        this.store
            .select(fromIts2Store.getIts2CatalogsServiceQuestions, { catalogGuid: catalog.catalogGuid })
            .pipe(
                filter((questions: CatalogEntryQuestion[]): questions is CatalogEntryQuestion[] => questions !== null && questions !== undefined),
                take(1),
                tap((questions: CatalogEntryQuestion[]) =>
                {
                    const isStep2Skipped = questions.length === 0;

                    this.store.dispatch(
                        fromRootStore.RootMiscStoreActions.RootWizard2StepEnabled({ stepIdx: 1, deactivated: isStep2Skipped })
                    );
                    this.store.dispatch(
                        fromIts2Store.Its2MiscActions.Its2RequestsServiceCreateSetSkipped({ idx: 2, isSkipped: isStep2Skipped })
                    );
                })
            )
            .subscribe();
    }
}
